import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  NgZone,
  OnInit,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidatorFn,
} from '@angular/forms';
import { CompanyTypeCountriesUsedFormGroup } from '@app/models/form.models';
import { LocalizedValue } from '@app/models/shared.models';
import { GenericDataService } from '@app/services/generic-data.service';
import { ButtonComponent } from '@app/ui-components/button/button.component';
import { GridTableComponent } from '@app/ui-components/grid-table/grid-table.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ColDef } from 'ag-grid-community';

export interface CountryListProvider {
  getSelectedCountries(): string[];
}

//TODO: Add filter, select/deselect all and order by isSelected
@Component({
  selector: 'app-countries-used-tab',
  imports: [CommonModule, GridTableComponent, TranslatePipe, ButtonComponent],
  templateUrl: './countries-used-tab.component.html',
  styleUrl: './countries-used-tab.component.scss',
})
export class CountriesUsedTabComponent implements OnInit, CountryListProvider {
  public showButtons = input<boolean>(true);
  public countriesUsed = input<string[]>([]);
  public rootForm =
    input.required<FormGroup<CompanyTypeCountriesUsedFormGroup>>();

  public disableSave = input.required<boolean>();

  protected countries = signal<(LocalizedValue & { isChecked: boolean })[]>([]);
  protected columns: ColDef[] = [];

  protected countriesSelected = new FormControl(null, {
    validators: [this.isOneSelected(() => this.countries())],
  });

  protected submitted: boolean = false;
  protected touched: boolean = false;

  public emitCancel = output<void>();
  public emitSave = output<void>();

  private get countriesCtrl(): FormControl<string[]> {
    return this.rootForm().controls.countries;
  }

  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private service: GenericDataService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {
    effect(() => {
      const list = this.countries();
      if (!list.length) return;

      const used = this.countriesUsed() ?? [];
      const set = new Set(used);

      let changed = false;
      const next = list.map((c) => {
        const desired = set.has(c.code);
        if (c.isChecked !== desired) {
          changed = true;
          return { ...c, isChecked: desired };
        }
        return c;
      });

      if (changed) {
        untracked(() => this.countries.set(next));
      }
    });
  }

  private buildColumns(): void {
    this.columns = [
      {
        field: 'isChecked',
        headerName: '',
        width: 80,
        editable: true,
        filter: false,
        sort: 'desc',
        valueSetter: (params: any) => {
          const newValue =
            params.newValue === true || params.newValue === 'true';
          params.data.isChecked = newValue;

          const selected = this.getSelectedCountries();
          this.ngZone.run(() => {
            this.countriesCtrl.setValue(selected, { emitEvent: false });

            if (!this.touched) {
              this.touched = true;
              this.countriesCtrl.markAsTouched();
              this.countriesSelected.markAsTouched();
            }

            this.countriesSelected.updateValueAndValidity({ emitEvent: false });
            this.cdr.markForCheck();
          });

          return true;
        },
      },
      {
        field: 'description',
        headerName: this.translate.instant('admin.companyType.tabs.country'),
      },
    ];
  }

  ngOnInit(): void {
    this.buildColumns();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.buildColumns());

    this.service.loadCountriesAdmin().subscribe((res) => {
      const list = (res?.data ?? [])
        .filter((c: any) => c.isActive)
        .map((c: any) => ({ ...c, isChecked: false }));
      this.countries.set(list);
      this.cdr.markForCheck();
    });

    this.countriesSelected.markAsTouched();
    this.countriesSelected.updateValueAndValidity({ emitEvent: false });
  }

  isOneSelected(getList: () => { isChecked: boolean }[]): ValidatorFn {
    return (_: AbstractControl) => {
      const list = getList();
      return list.some((item) => item.isChecked)
        ? null
        : { atLeastOneRequired: true };
    };
  }

  getSelectedCountries(): string[] {
    return this.countries()
      .filter((c) => c.isChecked)
      .map((c) => c.code);
  }

  save() {
    this.emitSave.emit();
  }
  cancel() {
    this.emitCancel.emit();
  }
}
